/**
 * Client boot: GA4, Clarity, PostHog, Crisp.
 * Sentry boots separately in main.tsx (ErrorBoundary needs early init).
 * Crisp identity mirrors pickads-fe (email / nickname / token / page_url).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  getMonitoringConfig,
  type MonitoringConfig,
} from "../lib/monitoring-config";
import { screenKeyForPath, screenTitleForPath } from "../lib/page-title";
import { startWebVitals } from "../lib/web-vitals";
import {
  CRISP_SEGMENT_SAFEGEO_ADMIN,
  crispSessionRows,
  readShopifyStaffSub,
  type CrispVisitorContext,
} from "../lib/crisp-session";

type Props = {
  shop?: string | null;
  plan?: string | null;
  /** Shopify app API key (client id) - Crisp/PostHog context */
  appId?: string | null;
  email?: string | null;
  name?: string | null;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
    CRISP_TOKEN_ID?: string;
    __SG_POSTHOG__?: {
      capture?: (event: string, props?: object) => void;
      identify?: (id: string, props?: object) => void;
      register?: (props: object) => void;
    };
  }
}

export function MonitoringBoot({ shop, plan, appId, email, name }: Props) {
  const started = useRef(false);
  const location = useLocation();
  const pathRef = useRef("");
  const config = getMonitoringConfig();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    bootVendors(config, {
      shop,
      plan,
      appId,
      appHandle: config.appHandle,
      email,
      name,
      path: location.pathname,
    });
    startWebVitals();
    pathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot once; nav handled below
  }, []);

  useEffect(() => {
    if (!started.current) return;
    if (pathRef.current === location.pathname) return;
    pathRef.current = location.pathname;
    trackPage(config, location.pathname, { shop, plan });
    void syncCrispIdentity(config, {
      shop,
      plan,
      appId,
      email,
      name,
      path: location.pathname,
    });
  }, [location.pathname, config, shop, plan, appId, email, name]);

  // Refresh Crisp / PostHog identity when status loads
  useEffect(() => {
    if (!shop && !plan && !appId && !email) return;
    void syncIdentity(config, {
      shop,
      plan,
      appId,
      email,
      name,
      path: location.pathname,
    });
  }, [shop, plan, appId, email, name, config, location.pathname]);

  return null;
}

function baseTags(input: {
  shop?: string | null;
  plan?: string | null;
  appId?: string | null;
  appHandle: string;
}) {
  return {
    product: "safegeo",
    app_handle: input.appHandle,
    app_id: input.appId || undefined,
    plan: input.plan || "unknown",
    shop: input.shop || undefined,
  };
}

function visitorContext(
  ctx: {
    shop?: string | null;
    plan?: string | null;
    appId?: string | null;
    appHandle: string;
    email?: string | null;
    name?: string | null;
    path?: string;
    userId?: string | null;
  },
): CrispVisitorContext {
  const path = ctx.path || (typeof window !== "undefined" ? window.location.pathname : "");
  return {
    shop: ctx.shop,
    plan: ctx.plan,
    appId: ctx.appId,
    appHandle: ctx.appHandle,
    email: ctx.email,
    name: ctx.name,
    userId: ctx.userId,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    pagePath: path,
    screen: screenKeyForPath(path),
  };
}

function bootVendors(
  config: MonitoringConfig,
  ctx: {
    shop?: string | null;
    plan?: string | null;
    appId?: string | null;
    appHandle: string;
    email?: string | null;
    name?: string | null;
    path: string;
  },
) {
  const tags = baseTags(ctx);

  if (config.ga4.enabled && config.ga4.measurementId) {
    const id = config.ga4.measurementId;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", id, {
      send_page_view: true,
      ...tags,
    });
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
  }

  if (config.clarity.enabled && config.clarity.projectId) {
    void import("@microsoft/clarity")
      .then((mod) => {
        const Clarity = mod.default;
        Clarity.init(config.clarity.projectId);
        Clarity.setTag("product", "safegeo");
        Clarity.setTag("app_handle", ctx.appHandle);
        if (ctx.appId) Clarity.setTag("app_id", ctx.appId);
        if (ctx.plan) Clarity.setTag("plan", ctx.plan);
        if (ctx.shop) {
          Clarity.setTag("shop", ctx.shop);
          Clarity.identify(ctx.shop, undefined, ctx.path, ctx.shop);
        }
      })
      .catch(() => {
        /* blocked */
      });
  }

  if (config.crisp.enabled && config.crisp.websiteId) {
    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = config.crisp.websiteId;
    // Stable token before script load when shop known (avoids anonymous visitorN).
    if (ctx.shop) {
      window.CRISP_TOKEN_ID = `safegeo:${ctx.shop}`;
    }
    // Final J4: no auto-open / chatbox preview
    window.$crisp.push(["safe", true]);
    window.$crisp.push(["config", "position:reverse", false]);
    if (!document.getElementById("safegeo-crisp")) {
      const s = document.createElement("script");
      s.id = "safegeo-crisp";
      s.async = true;
      s.src = "https://client.crisp.chat/l.js";
      document.head.appendChild(s);
    }
    void pushCrispIdentity(ctx);
  }

  if (config.posthog.enabled && config.posthog.key) {
    void import("posthog-js")
      .then(({ default: posthog }) => {
        posthog.init(config.posthog.key, {
          api_host: config.posthog.host,
          person_profiles: "identified_only",
          capture_pageview: false,
          capture_pageleave: true,
        });
        if (ctx.shop) {
          posthog.identify(ctx.shop, {
            shop: ctx.shop,
            plan: ctx.plan || undefined,
            product: "safegeo",
            app_handle: ctx.appHandle,
            app_id: ctx.appId || undefined,
            email: ctx.email || undefined,
          });
        }
        posthog.register(tags);
        posthog.capture("$pageview", {
          path: ctx.path,
          screen: screenKeyForPath(ctx.path),
          title: screenTitleForPath(ctx.path),
          ...tags,
        });
        window.__SG_POSTHOG__ = posthog;
      })
      .catch(() => {
        /* missing */
      });
  }
}

async function pushCrispIdentity(ctx: {
  shop?: string | null;
  plan?: string | null;
  appId?: string | null;
  appHandle: string;
  email?: string | null;
  name?: string | null;
  path?: string;
}) {
  if (!window.$crisp) return;

  const staffSub = await readShopifyStaffSub();
  const tokenId = staffSub
    ? `safegeo:${ctx.shop || "shop"}:${staffSub}`
    : ctx.shop
      ? `safegeo:${ctx.shop}`
      : null;
  if (tokenId) {
    window.CRISP_TOKEN_ID = tokenId;
  }

  const nickname =
    (ctx.name || "").trim() ||
    (ctx.shop || "").replace(/\.myshopify\.com$/i, "") ||
    "";
  const email = (ctx.email || "").trim();

  if (nickname) {
    window.$crisp.push(["set", "user:nickname", [nickname]]);
  }
  if (email) {
    // Second arg false = do not verify via Crisp email link (embedded admin).
    window.$crisp.push(["set", "user:email", [email, false]]);
  }
  if (ctx.shop) {
    window.$crisp.push(["set", "user:company", [ctx.shop]]);
  }

  window.$crisp.push([
    "set",
    "session:segments",
    [[CRISP_SEGMENT_SAFEGEO_ADMIN], true],
  ]);

  const rows = crispSessionRows(
    visitorContext({
      ...ctx,
      userId: staffSub || (ctx.shop ? `shop:${ctx.shop}` : null),
    }),
  );
  if (rows.length) {
    window.$crisp.push(["set", "session:data", [rows]]);
  }
}

async function syncCrispIdentity(
  config: MonitoringConfig,
  ctx: {
    shop?: string | null;
    plan?: string | null;
    appId?: string | null;
    email?: string | null;
    name?: string | null;
    path: string;
  },
) {
  if (!config.crisp.enabled) return;
  await pushCrispIdentity({
    ...ctx,
    appHandle: config.appHandle,
  });
}

async function syncIdentity(
  config: MonitoringConfig,
  ctx: {
    shop?: string | null;
    plan?: string | null;
    appId?: string | null;
    email?: string | null;
    name?: string | null;
    path: string;
  },
) {
  await syncCrispIdentity(config, ctx);

  const ph = window.__SG_POSTHOG__;
  if (ph?.identify && ctx.shop) {
    ph.identify(ctx.shop, {
      shop: ctx.shop,
      plan: ctx.plan || undefined,
      product: "safegeo",
      app_handle: config.appHandle,
      app_id: ctx.appId || undefined,
      email: ctx.email || undefined,
    });
  }
  if (ph?.register) {
    ph.register(
      baseTags({
        shop: ctx.shop,
        plan: ctx.plan,
        appId: ctx.appId,
        appHandle: config.appHandle,
      }),
    );
  }

  if (config.clarity.enabled && ctx.shop) {
    void import("@microsoft/clarity")
      .then((mod) => {
        const Clarity = mod.default;
        Clarity.identify(ctx.shop!, undefined, ctx.path, ctx.shop!);
        if (ctx.plan) Clarity.setTag("plan", ctx.plan);
        if (ctx.appId) Clarity.setTag("app_id", ctx.appId);
      })
      .catch(() => {
        /* ignore */
      });
  }
}

function trackPage(
  config: MonitoringConfig,
  path: string,
  ctx: { shop?: string | null; plan?: string | null },
) {
  const screen = screenKeyForPath(path);
  const title = screenTitleForPath(path);

  if (config.ga4.enabled && window.gtag) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      screen,
      product: "safegeo",
      shop: ctx.shop || undefined,
      plan: ctx.plan || undefined,
    });
  }

  if (window.__SG_POSTHOG__?.capture) {
    window.__SG_POSTHOG__.capture("$pageview", {
      path,
      screen,
      title,
      product: "safegeo",
      plan: ctx.plan || "unknown",
      shop: ctx.shop || undefined,
    });
  }

  if (config.clarity.enabled) {
    void import("@microsoft/clarity")
      .then((mod) => {
        const Clarity = mod.default;
        if (ctx.shop) Clarity.identify(ctx.shop, undefined, path, ctx.shop);
        Clarity.setTag("path", path);
        Clarity.setTag("screen", screen);
      })
      .catch(() => {
        /* ignore */
      });
  }
}

/** Product events (setup funnel, upgrade, etc.) */
export function trackSafegeoEvent(
  event: string,
  props: Record<string, unknown> = {},
): void {
  try {
    window.__SG_POSTHOG__?.capture?.(event, {
      product: "safegeo",
      ...props,
    });
  } catch {
    /* ignore */
  }
  try {
    if (window.gtag) {
      window.gtag("event", event, { product: "safegeo", ...props });
    }
  } catch {
    /* ignore */
  }
}

/** Refresh page_url then open Crisp (Help CTA). */
export function openCrispChat(): void {
  const crisp = window.$crisp;
  if (!crisp) return;
  try {
    const href = window.location.href;
    const path = window.location.pathname;
    crisp.push([
      "set",
      "session:data",
      [
        [
          ["page_url", href],
          ["page_path", path],
          ["screen", screenKeyForPath(path)],
        ],
      ],
    ]);
  } catch {
    /* ignore */
  }
  crisp.push(["do", "chat:open"]);
}
